package terraultima

import kotlinx.serialization.json.Json

object Content {
    lateinit var corpus: Corpus
        private set
    val loaded: Boolean get() = this::corpus.isInitialized
    private val byId = mutableMapOf<String, GameEvent>()
    private val mods = mutableMapOf<String, Module>()
    private val pressureIndex = mutableMapOf<String, MutableList<GameEvent>>()
    private val sourceIndex = mutableMapOf<String, MutableList<GameEvent>>()

    private val json = Json { ignoreUnknownKeys = true; isLenient = true; allowComments = true }

    /** The landing spoken of as past: "since landfall", "since we landed", "since the landing". */
    private val LANDED_PAST = Regex("""\bsince (landfall|we landed|the landing|touchdown)\b""",
        RegexOption.IGNORE_CASE)

    /** The six files the corpus is made of, in the order nothing depends on. */
    val FILES = listOf("capabilities.json", "modules.json", "builds.json", "planets.json",
        "events.json", "catastrophes.json")

    /**
     * Parse the corpus from text that somebody else fetched.
     *
     * The file reading used to be inline here, which made this object — and therefore the whole
     * engine that depends on it — JVM-only. A browser has no filesystem and fetches over HTTP; a
     * JVM reads a directory (`ContentFiles.kt`, which keeps `Content.load()` working unchanged).
     * Neither belongs in the thing that knows what an event IS.
     */
    fun loadFrom(texts: Map<String, String>) {
        fun read(n: String) = texts[n] ?: error("corpus is missing $n")
        val c = json.decodeFromString<Corpus>(read("capabilities.json"))
        val m = json.decodeFromString<Corpus>(read("modules.json"))
        val b = json.decodeFromString<Corpus>(read("builds.json"))
        val p = json.decodeFromString<Corpus>(read("planets.json"))
        val e = json.decodeFromString<Corpus>(read("events.json"))
        val k = json.decodeFromString<Corpus>(read("catastrophes.json"))
        corpus = Corpus(c.capabilities, m.modules, b.builds, p.planets, e.events, k.catastrophes)
        index()
    }

    private fun index() {
        stageIndex = corpus.events.filter { it.stageOf.isNotBlank() }.groupBy { it.stageOf }
        byId.clear(); mods.clear(); pressureIndex.clear(); sourceIndex.clear()
        corpus.events.forEach { ev ->
            byId[ev.id] = ev
            ev.pressures.keys.forEach { pressureIndex.getOrPut(it) { mutableListOf() }.add(ev) }
            sourceIndex.getOrPut(ev.source) { mutableListOf() }.add(ev)
        }
        corpus.modules.forEach { mods[it.id] = it }
    }

    /**
     * SCALING EXPERIMENT (see docs/03-PLAYTEST-METHOD.md).
     *
     * Several metrics — deck starvation, repetition, replay variety — could be failing either
     * because the ARCHITECTURE is wrong or simply because a 84-event prototype corpus is 12x
     * smaller than the ~1000 events the real game will ship. Those two explanations demand
     * opposite responses, so guessing is not acceptable. This clones every pressure-sourced event
     * into N near-identical variants with distinct ids, giving a corpus of the target size with the
     * same structure. If the metrics converge on their targets as N grows, the architecture is
     * sound and the gap is a content-quantity gap. If they do not, the architecture is at fault.
     *
     * Cloning is a measurement device, NOT content: variants are textually identical, so this tells
     * us nothing about whether 1000 events would feel varied — only whether the machinery scales.
     */
    /** Test/tooling only: swap the corpus so the validator can be tested against bad content. */
    fun setCorpus(c: Corpus) { corpus = c; index() }

    fun inflate(n: Int) {
        if (n <= 1) return
        val extra = mutableListOf<GameEvent>()
        for (e in corpus.events) {
            if (e.source != "PRESSURE") continue
            for (i in 2..n) extra.add(e.copy(id = "${e.id}__v$i"))
        }
        corpus = corpus.copy(events = corpus.events + extra)
        index()
    }

    fun event(id: String) = byId[id]
    fun module(id: String) = mods[id]
    /** Events that can stand in for a chain beat, for the pluggable-stage draw in the Director. */
    fun standIns(beat: String): List<GameEvent> = stageIndex[beat] ?: emptyList()

    fun capability(id: String) = corpus.capabilities.firstOrNull { it.id == id }

    /**
     * The ONE name the player sees for a capability, everywhere: the CAN DO list, a lock reason,
     * an option hint. Falls back to the verb, then to the id, so a corpus that has not been
     * regenerated still reads as English rather than as SCREAMING_SNAKE.
     */
    fun capShort(id: String): String = capability(id)?.short?.takeIf { it.isNotBlank() }
        ?: capability(id)?.verb?.substringBefore(" (") ?: id.lowercase().replace('_', ' ')

    fun capVerb(id: String): String =
        capability(id)?.verb?.substringBefore(" (") ?: id.lowercase().replace('_', ' ')
    fun byPressure(p: String): List<GameEvent> = pressureIndex[p] ?: listOf()
    fun bySource(s: String): List<GameEvent> = sourceIndex[s] ?: listOf()
    fun byKind(k: String): List<GameEvent> = corpus.events.filter { it.kind == k }
    fun build(id: String) = corpus.builds.first { it.id == id }
    fun catastrophe(id: String) = corpus.catastrophes.first { it.id == id }
    fun planet(id: String) = corpus.planets.first { it.id == id }

    private var stageIndex: Map<String, List<GameEvent>> = emptyMap()

    private val SCHED_RE = Regex("""^schedule:([A-Za-z0-9_]+)\s+in\s+[0-9]+(\.\.[0-9]+)?y$""")
    private val FLAG_RE = Regex("""^flag:[A-Za-z0-9_]+\s*=\s*(true(\s+for\s+[0-9]+y)?|false)$""")
    private val DAMAGE_RE = Regex("""^damage:\S+\s+-?[0-9.]+$""")
    private val NUM_RE = Regex("""^([A-Za-z_][A-Za-z0-9_.]*)\s*(\+=|-=|=)\s*(-?[0-9.]+)$""")

    private val READABLE: Set<String> =
        listOf("food", "water", "spares", "medicine", "deltaV", "cultivated", "data", "energy").map { "res.$it" }
            .plus(listOf("adults", "children", "elders", "cryo", "total").map { "pop.$it" })
            .plus(GAUGES.map { "gauge.$it" })
            .plus(PRESSURES.map { "pressure.$it" })
            .plus(AXES.keys.map { "axis.$it" })
            .plus(listOf("gravity", "pressure", "temp", "water", "flora", "fauna", "seismic",
                         "toxicity", "insolation", "ore", "dayLength").map { "planet.$it" })
            .plus(listOf("sim.powerRatio", "sim.broken", "sim.fed", "sim.deaths",
                         "sim.deathsNotable", "sim.settledYears", "sim.foodTrend",
                         "sim.shelterShare", "year", "month"))
            .toSet()

    /**
     * Phrasings by which a body claims THE COLONY HAS A PAST: repetition ("again", "last time",
     * "four times"), duration ("for years", "has been weeping"), or a first ("the first death").
     * Each is only true of a colony that has lived long enough for it to be true.
     */
    private val PRESUPPOSES = Regex(
        """(?<!happen )\bagain\b|\b(for|in) (years|months|a long time)\b""" +
        """|\b(has|have) been \w+(ed|ing)\b|\b(since|always has|used to|no longer|by now)\b""" +
        """|\ba generation\b|\b(stopped|started) \w+ing\b|\blast time\b""" +
        """|\bthe first (death|time|sample|night)\b|\bfor the first time\b""" +
        """|\b(two|three|four|five|six|nine|ten|eleven|twelve) times\b|\bdeferred\b""",
        RegexOption.IGNORE_CASE)

    /**
     * Phrasings by which a body points at A DECISION THE PLAYER MADE. Referring to one the player
     * never made is the worst version of an ungrounded premise: it does not merely describe a
     * colony that does not exist, it blames the player for it. Three events did exactly this
     * (`PLAYTEST-LOG.md` §1e.1) and each was fixed by requiring the flag of the decision.
     */
    private val NAMES_A_DECISION = Regex(
        """\byou (decided|endorsed|ordered|promised|granted|approved|refused|banned|chose|told|signed)\b""" +
        """|\bwhat you (did|said|knew|decided)\b""" +
        """|\bthe \w+ you (endorsed|ordered|approved|granted|promised|signed|wrote|refused)\b""" +
        """|\byour (order|decision|ruling|promise|policy|refusal|rule|law)\b|\bquote you\b""",
        RegexOption.IGNORE_CASE)

    /**
     * Bodies that claim the colony has DEPARTED FROM ITS NORMAL STATE — a surplus, an idle machine,
     * something unassigned, a first. Such a claim is false of a ship as launched: the launch reserve
     * IS the plan, the reactor is sized for the colony it will become, and nothing is unassigned on
     * a design three months old. Distinct from a plain statement of fact ("the antenna has bandwidth
     * to spare"), which is true at launch and fine.
     */
    private val CLAIMS_DEPARTURE = Regex(
        """\bfor once\b|\bfuller than\b|\bloafing\b|\bsurplus\b|\bahead of schedule\b""" +
        """|\bnobody has (assigned|used)\b|\bmore than (you|we|it) need""" +
        """|\bfor the first time in\b|\bnothing else to do\b""",
        RegexOption.IGNORE_CASE)

    /**
     * WORDS THE ENGINE OWNS. `pressure` is the name of the hidden-state layer, so a body that means
     * atmospheric pressure ("Shelter and pressure, before anything else") hands the player our
     * jargon and lets them read a physical fact as a system term. The physical ideas all have other
     * words — a sealed roof, seals, air, wear — and the systems do not.
     *
     * `demand` and `drift` are deliberately absent: "generation is below demand" is ordinary
     * engineering English and unambiguous in context. Module NAMES are exempt (a "rigid pressure
     * hull" is hardware nomenclature); this checks the text a player reads inside an event.
     */
    private val RESERVED_WORDS = Regex("""\b(pressures?|tension|entropy|gauges?)\b""",
        RegexOption.IGNORE_CASE)

    /**
     * An option promising that something is settled for good. The engine can keep such a promise
     * only through a flag, a module, an ambition or a phase change; in prose alone it is a
     * consequence the next decade cannot see. \u00a714.1.
     */
    /**
     * Nouns that ARE one of the eight stocks, not nouns a stock might be involved in. "Water" is
     * deliberately absent: it is scenery in a third of the corpus ("where the water sits in
     * spring") and would drown the advisory in noise.
     */
    private val STOCK_NOUNS = listOf(
        "deltaV" to Regex("""\b(propellant|ascent fuel|ascent tank|ascent load)\b""", RegexOption.IGNORE_CASE),
        "medicine" to Regex("""\b(the medicine|the drugs|the pharmacy stock)\b""", RegexOption.IGNORE_CASE),
        "spares" to Regex("""\b(the spares|the parts bin|the spares depot)\b""", RegexOption.IGNORE_CASE))

    private val FOREVER = Regex(
        """\bforever\b|\bpermanently\b|\bfor good\b|\bever again\b|\bnever again\b""" +
        """|\bindefinitely\b|\b(nobody|no one|nothing) [a-z ]{0,20}\bagain\b""",
        RegexOption.IGNORE_CASE)

    /**
     * A `requires` term that can tell a worn colony from one three months out of dock.
     * `cap:`, `planet.` and `phase` cannot: they are true at launch and true at year ninety.
     */
    private val HISTORY_GATE = Regex("""^!?(pressure\.|flag:|pop\.|res\.|sim\.|gauge\.|year|month)""")

    /**
     * Returns null if a CONDITION reads something the engine can actually evaluate.
     * Effects were checked from the start; conditions were not, so `planet.ore >= 0.55` passed
     * validation and then threw at runtime the first time the event became eligible.
     */
    private fun checkCondition(rawIn: String): String? {
        for (alt in rawIn.split('|')) {
            val c = alt.trim().trimStart('!')
            if (c.startsWith("cap:") || c.startsWith("flag:")) continue
            if (c.startsWith("phase ")) continue
            val m = Regex("""^([A-Za-z_][A-Za-z0-9_.]*)\s*(<=|>=|==|!=|<|>)""").find(c) ?: return "unparseable condition '$c'"
            val path = m.groupValues[1]
            if (path == "phase" || path.contains("{")) continue
            if (path !in READABLE) return "'$path' is not a readable path"
        }
        return null
    }

    /** Returns null if the effect is well-formed and targets something real; else the complaint. */
    private fun checkEffect(rawIn: String): String? {
        val raw = rawIn.trim()
        if (SCHED_RE.matches(raw) || FLAG_RE.matches(raw) || DAMAGE_RE.matches(raw)) return null
        if (raw.startsWith("kill:") || raw.startsWith("thread.var:")) return null
        if (raw.startsWith("ambition:")) {
            val id = raw.removePrefix("ambition:").trim()
            return if (AMBITIONS.any { it.id == id }) null else "unknown ambition '$id'"
        }
        if (raw.startsWith("phase:")) {
            return if (raw.removePrefix("phase:").trim() in listOf("SPACE", "ORBIT", "SURFACE")) null
            else "unknown phase in '$raw'"
        }
        if (raw.startsWith("install:")) {
            val id = raw.removePrefix("install:").trim()
            return if (mods.containsKey(id)) null else "install: names no such module '$id'"
        }
        if (raw.startsWith("grant:") || raw.startsWith("revoke:")) {
            val c = raw.substringAfter(':').trim()
            return if (corpus.capabilities.any { it.id == c }) null
            else "${raw.substringBefore(':')}: names no such capability '$c'"
        }
        val m = NUM_RE.find(raw) ?: return "unparseable effect '$raw'"
        val path = m.groupValues[1]
        // Slot-bound paths are resolved at runtime and cannot be checked here.
        if (path.contains("{")) return null
        return if (path in Expr.WRITABLE) null
        else "'$path' is not a writable path (it would silently do nothing). Valid: ${Expr.WRITABLE.sorted().take(4)}…"
    }

    /**
     * Content validation. These rules are not style preferences — each one is a design invariant
     * from a decision doc, enforced mechanically because at 1000 events nobody will check by hand.
     */
    val advisories = mutableListOf<String>()

    // No inline (?i): the same corpus is validated in the browser, and JavaScript rejects it.
    internal val SUPPLY_CLAIM = Regex(
        "\\b[a-z0-9]+\\s+(day|week|month|year)s?\\s+of\\s+(food|rations|calories|stores)\\b",
        RegexOption.IGNORE_CASE)

    fun validate(): List<String> {
        val errs = mutableListOf<String>()
        advisories.clear()
        val capIds = corpus.capabilities.map { it.id }.toSet()
        val ids = mutableSetOf<String>()

        for (e in corpus.events) {
            if (!ids.add(e.id)) errs.add("[dup-id] ${e.id}")
            if (e.body.isBlank()) errs.add("[no-body] ${e.id}")

            // A LIGHT MOBILE GAME READS ONE SCREEN AT A TIME. The caps are ratchets set at the
            // longest text in the corpus when they were introduced (2026-09-25), so nothing can get
            // heavier; the advisories are where the copy is meant to go (docs/12-MINIAPP.md §4).
            val words = { t: String -> t.split(Regex("\\s+")).count { it.isNotBlank() } }
            if (words(e.body) > 60) errs.add("[long-body] ${e.id}: ${words(e.body)} words, the cap is 60")
            else if (words(e.body) > 40) advisories.add("[heavy-body] ${e.id}: ${words(e.body)} words, aim for 40")
            for (o in e.options) {
                if (words(o.label) > 13) errs.add("[long-label] ${e.id}: \"${o.label}\" is ${words(o.label)} words, the cap is 13")
                if (words(o.hint) > 30) errs.add("[long-hint] ${e.id}: \"${o.label}\" hint is ${words(o.hint)} words, the cap is 30")
            }
            // THE INTERFACE SAYS HOW LONG FOOD LASTS; THE TEXT MAY NOT. Food is person-months, so
            // "eleven weeks of food" is true at one headcount only: the seed-stock option said it
            // and delivered four days at 169 people. The option's price line already converts the
            // effect at the current headcount, so a duration in prose can only ever disagree with it.
            for (t in listOf(e.body) + e.options.flatMap { listOf(it.label, it.hint) }) {
                SUPPLY_CLAIM.find(t)?.let {
                    errs.add("[stated-supply] ${e.id}: \"${it.value}\" -- the price line states durations; say what it is, not how long it lasts")
                }
            }

            // D002 L4: every event that ASKS something must always offer at least 2 ungated options,
            // or the player can be handed a decision with nothing to decide.
            // Symptoms are allowed to be pure information — that is their whole job (D003 §3.3).
            val asksSomething = e.options.isNotEmpty() || e.kindE == EventKind.DILEMMA || e.kindE == EventKind.CRISIS
            if (asksSomething) {
                val ungated = e.options.count { it.requires.isEmpty() }
                if (ungated < 2) errs.add("[floor] ${e.id} has $ungated ungated options, needs >= 2")
                if (e.options.size < 2) errs.add("[too-few] ${e.id} has ${e.options.size} options")
                // THE CAP IS ABOUT WHAT A PLAYER READS AT ONCE, NOT WHAT AN AUTHOR WROTE.
                //
                // Two options gated on `flag:x` and `!flag:x` can never appear on the same screen,
                // so counting them both against a legibility limit is counting a screen that does
                // not exist. This matters as soon as consequence links exist (D009): the natural
                // shape is an option that REPLACES another once an earlier decision was taken --
                // "keep the date you named" standing where "take the first ground" stood.
                //
                // Conservative: an option only earns the exemption if some OTHER option in the
                // same event requires the exact negation of a flag it requires.
                val flagsOf = { o: Option -> o.requires.flatMap { it.split('|') }.map { it.trim() }
                    .filter { it.startsWith("flag:") || it.startsWith("!flag:") }.toSet() }
                val allGates = e.options.flatMap { flagsOf(it) }.toSet()
                val exclusive = e.options.count { o ->
                    flagsOf(o).any { g ->
                        val opposite = if (g.startsWith("!")) g.removePrefix("!") else "!$g"
                        allGates.contains(opposite)
                    }
                }
                val onScreen = e.options.size - exclusive / 2
                if (onScreen > 6) errs.add("[too-many] ${e.id} has ${e.options.size} options " +
                        "($onScreen can appear at once, max 6)")
            }

            // D002 §6: capability soup makes an option effectively unreachable.
            for (o in e.options) {
                // A requirement may be an alternation: "cap:SCAN_ATMO | cap:DRONE" means either will
                // do. That counts as ONE gate, not two — it widens reachability rather than
                // narrowing it, which is the opposite of capability soup.
                val caps = o.requires.filter { it.trimStart('!').startsWith("cap:") }
                if (caps.size > 2) errs.add("[cap-soup] ${e.id} / '${o.label}' requires ${caps.size} caps")
                for (r in o.requires) for (alt in r.split('|')) {
                    val a = alt.trim().trimStart('!')
                    if (!a.startsWith("cap:")) continue
                    val c = a.removePrefix("cap:")
                    if (c !in capIds) errs.add("[unknown-cap] ${e.id} / '${o.label}' -> $c")
                }
                if (o.hint.isBlank()) errs.add("[no-hint] ${e.id} / '${o.label}' (Clarity A3: state intent+cost)")
                // A misspelt visibility silently falls back to AUTO, which is the failure mode a
                // closed vocabulary exists to prevent: the corpus grows, the validator stays green,
                // and the new field does nothing (red-team F4).
                if (o.whenLocked.lowercase() !in setOf("auto", "show", "name", "hide"))
                    errs.add("[bad-when-locked] ${e.id} / '${o.label}' -> '${o.whenLocked}' " +
                            "is not one of auto, show, name, hide")
                // An ungated option can never be locked, so saying what it looks like when it is
                // locked is either a misunderstanding or a gate somebody forgot to write.
                if (o.requires.isEmpty() && o.lockedAs != WhenLocked.AUTO)
                    errs.add("[pointless-when-locked] ${e.id} / '${o.label}' has no gates, so " +
                            "whenLocked='${o.whenLocked}' can never apply")
                if (o.effects.isEmpty() && o.outcomes.isEmpty())
                    errs.add("[fake-choice] ${e.id} / '${o.label}' does nothing (A5)")
                // Probabilities must sum to 1 WITHIN EACH BRANCH GROUP. Conditional outcomes (F9)
                // partition an option into alternative worlds — "with a drone" and "without one" —
                // and each world's branches are drawn among themselves after the others are
                // filtered out, so summing them all together is meaningless.
                val groups = o.outcomes.groupBy { it.requires.sorted() }
                for ((gate, outs) in groups) {
                    val sum = outs.sumOf { it.p }
                    if (kotlin.math.abs(sum - 1.0) > 0.02)
                        errs.add("[bad-probs] ${e.id} / '${o.label}' outcomes for " +
                                (if (gate.isEmpty()) "the unconditional branch" else gate.joinToString(" + ")) +
                                " sum to $sum")
                }
                // An option whose every branch is conditional can find itself with nothing to draw.
                // The engine falls back to the unconditional set; if there is none, it silently
                // does nothing at all — which is `[fake-choice]` arriving at runtime instead of
                // at build time.
                if (o.outcomes.isNotEmpty() && o.outcomes.all { it.requires.isNotEmpty() })
                    errs.add("[no-default-outcome] ${e.id} / '${o.label}' has only conditional " +
                            "outcomes, so a state matching none of them resolves to nothing; " +
                            "leave one branch ungated")
                for (oc in o.outcomes) for (r in oc.requires)
                    checkCondition(r)?.let { errs.add("[bad-condition] ${e.id} / '${o.label}' outcome: $it") }
            }

            // A SHIP THAT HAS NOT LANDED HAS NO LANDFALL BEHIND IT. "The rotas have not had a gap
            // in them since landfall" fired in year 8 of a fifteen-year crossing (playtest
            // 2026-09-25). An event that can fire in space may not speak of the landing as past
            // unless it requires having landed.
            val canFlyEvent = e.phase.any { it == "ANY" || it == "SPACE" || it == "ORBIT" } &&
                    e.requires.none { it == "flag:landed" || it == "flag:settled" }
            if (canFlyEvent) for (t in listOf(e.body) + e.options.map { it.hint }) {
                LANDED_PAST.find(t)?.let {
                    errs.add("[unlanded-landfall] ${e.id} can fire in space but says '${it.value}'")
                }
            }

            // D003 P5: identity may never be written directly.
            for (o in e.options) for (eff in o.effects)
                if (eff.startsWith("axis.")) errs.add("[axis-write] ${e.id} writes an axis directly")

            // Closed vocabularies. Everything below used to fail SILENTLY, which is the single
            // most dangerous property a content pipeline can have: the corpus grows, the validator
            // stays green, and the new content does nothing. (Red-team F4.)
            for (o in e.options) {
                for (t in o.tags)
                    if (t !in Expr.TAGS) errs.add("[unknown-tag] ${e.id} / '${o.label}' -> '$t' is not in the 16-tag vocabulary")
                for (eff in o.effects + o.outcomes.flatMap { it.effects }) {
                    val v = checkEffect(eff)
                    if (v != null) errs.add("[bad-effect] ${e.id} / '${o.label}': $v")
                }
                // Magnitude sanity. An LLM asked for "a significant effect" writes 500.
                for (eff in o.effects + o.outcomes.flatMap { it.effects }) {
                    val m = Regex("""^(pressure|gauge)\.([A-Za-z]+)\s*(\+=|-=)\s*([0-9.]+)$""").find(eff.trim())
                    if (m != null) {
                        val ns = m.groupValues[1]; val n = m.groupValues[4].toDouble()
                        val cap = if (ns == "pressure") 0.35 else 20.0
                        if (n > cap) errs.add("[oversized-effect] ${e.id} / '${o.label}': $eff exceeds the per-decision cap ($cap)")
                    }
                }
            }
            if (e.weight > 4.0 || e.weight <= 0.0)
                errs.add("[bad-weight] ${e.id} weight=${e.weight}; weights are relative within a deck, keep them 0.1-4")
            // An OPPORTUNITY must carry NO pressure tags: pressure tags are what put an event into a
            // themed deck, and an opportunity is by definition what the colony does when no pressure
            // is speaking. A tagged opportunity would leak into the crisis channel.
            // An OPPORTUNITY opens by telling the player their colony has slack ("there is power to
            // spare", "the stores are fuller than the plan requires"). If it does not REQUIRE that
            // slack, it will eventually say so to a colony that is starving — and an event that
            // misdescribes the player's own colony is worse than no event.
            // An event that can recur inside a player's memory needs alternate wording, or the
            // second occurrence reads as a bug rather than as weather.
            if (!e.once && e.cooldownYears < 15 && e.variants.isEmpty() &&
                e.kindE != EventKind.SYMPTOM && e.kindE != EventKind.NOTICE)
                advisories.add("[no-variants] ${e.id} can recur every ${e.cooldownYears}y with identical text")
            if (e.kindE == EventKind.OPPORTUNITY && e.requires.isEmpty())
                errs.add("[unfounded-opportunity] ${e.id} claims the colony has slack but requires none")
            if (e.kindE == EventKind.OPPORTUNITY && e.pressures.isNotEmpty())
                errs.add("[tagged-opportunity] ${e.id} carries pressure tags; opportunities are drawn when tension is LOW")
            if ((e.kindE == EventKind.DILEMMA || e.kindE == EventKind.OPPORTUNITY) && e.cooldownYears < 6)
                errs.add("[short-cooldown] ${e.id} cooldownYears=${e.cooldownYears}; a dilemma may not recur inside 6 years")

            // THE PREMISE RULE (docs/04-CONTENT-AUTHORING.md §9). An event whose body reports history must REQUIRE the
            // history. Playtest 2026-08-28: the first event of a new game was "A petition of
            // thirty names" demanding an end to the closed inquiries the player had not yet had
            // the chance to hold. It sat in the dissent deck from launch because a motive starts
            // dissent above the demand floor and the event required nothing at all.
            //
            // This catches only the LEXICAL tail of the class — a body that says "again" or "for
            // years". The petition itself said neither; its presupposition was semantic, and no
            // regex will find that one. `premise_ledger.py --opening` prints the first-year deck
            // for a human to read, which is the half of this that cannot be automated.
            if (e.body.isNotBlank() && e.requires.none { HISTORY_GATE.containsMatchIn(it.trim()) })
                PRESUPPOSES.find(e.body)?.let {
                    errs.add("[ungrounded-premise] ${e.id} says '${it.value}' — a claim about the " +
                            "colony's past — but requires no state that a new colony lacks")
                }

            // ...and the sharper case: a body that names something the PLAYER did must require the
            // flag that records the player doing it. Symptoms are exempt: they are colony mood in
            // the second person ("closer to the truth than what you told them"), not an accusation
            // about one ruling, and they are already gated on the pressure they announce.
            if (e.kindE != EventKind.SYMPTOM &&
                e.requires.none { it.contains("flag:") })
                NAMES_A_DECISION.find(e.body)?.let {
                    errs.add("[unattributed-decision] ${e.id} says '${it.value}' but requires no " +
                            "flag — it can tell the player about a decision they never made")
                }

            // §13.1 — the engine's vocabulary may not appear in the text a player reads.
            for ((where, text) in listOf("body" to e.body) +
                    e.options.map { "option '${it.label}'" to (it.label + " " + it.hint) } +
                    e.variants.mapIndexed { i, v -> "variant $i" to v.body })
                RESERVED_WORDS.find(text)?.let {
                    errs.add("[reserved-word] ${e.id} / $where says '${it.value}' \u2014 that word " +
                            "names an engine system; say what the sentence means instead " +
                            "(a sealed roof, seals, air, wear)")
                }

            // §13.3 — a deferred consequence belongs to the road NOT taken. "Power first" scheduled
            // `power_crisis`, the one thing it prevents; read cold, the game punishes the player
            // for the priority they chose. Flagged when an option schedules an event whose own
            // preconditions name something this option just improved.
            for (o in e.options) {
                val improved = o.effects.mapNotNull { eff ->
                    Regex("""^(res|gauge)\.([A-Za-z]+)\s*\+=""").find(eff.trim())?.groupValues?.get(2)
                }.toSet()
                for (eff in o.effects) {
                    if (!eff.trim().startsWith("schedule:")) continue
                    val target = eff.trim().removePrefix("schedule:").split(" ")[0]
                    val ev = corpus.events.firstOrNull { it.id == target } ?: continue
                    val about = ev.requires.mapNotNull { r ->
                        Regex("""(res|sim)\.([A-Za-z]+)""").find(r)?.groupValues?.get(2)
                    }.map { if (it == "powerRatio") "energy" else if (it == "fed") "food" else it }
                    if (about.any { improved.contains(it) }) advisories.add(
                        "[self-cancelling-schedule] ${e.id} / '${o.label}' improves " +
                                "${improved.intersect(about.toSet())} and then schedules '$target', " +
                                "which is about the same thing \u2014 a deferred consequence should " +
                                "belong to what this option DEFERRED")
                }
            }

            for (r in e.requires) checkCondition(r)?.let { errs.add("[bad-condition] ${e.id}: $it") }
            for (o in e.options) for (r in o.requires)
                checkCondition(r)?.let { errs.add("[bad-condition] ${e.id} / '${o.label}': $it") }
            for (p in e.pressures.keys) if (p !in PRESSURES) errs.add("[unknown-pressure] ${e.id} -> $p")

            // A crisis may only claim a hidden cause that ACTUALLY GATES IT. Tagging a stochastic
            // component failure as "caused by entropy" is a lie the two-warnings rule then cannot
            // satisfy: the crisis fires at entropy 0.05 and no symptom ever preceded it. If the
            // pressure is a real precondition, say so in `requires`; if it is not, claim nothing.
            if (e.source == "SIM" || e.source == "EMITTED")
                for (p in e.pressures.keys)
                    if (e.requires.none { it.contains("pressure.$p") })
                        errs.add("[false-cause] ${e.id} claims '$p' as a cause but does not require it")
            if (e.kindE == EventKind.SYMPTOM && e.symptomOf !in PRESSURES)
                errs.add("[symptom-orphan] ${e.id} symptomOf='${e.symptomOf}'")

            // D001 (amended, see D001 §7): a pressure-driven event may kill ONLY inside a
            // probabilistic outcome branch of an option — i.e. only where the player knowingly
            // took a gamble. It may never kill as a flat consequence of choosing something.
            // The rule's purpose is that losses feel fair; a declared gamble is fair, an
            // undeclared one is not.
            if (e.source == "PRESSURE") for (o in e.options) {
                if (o.effects.any { it.startsWith("kill:") })
                    errs.add("[pressure-kills] ${e.id} / '${o.label}' — a pressure event may only kill inside a declared gamble (D001 §7)")
            }

            // schedule targets must exist
            for (o in e.options) for (eff in o.effects + o.outcomes.flatMap { it.effects })
                if (eff.startsWith("schedule:")) {
                    val t = eff.removePrefix("schedule:").split(" ")[0]
                    if (t !in corpus.events.map { it.id }) errs.add("[dangling-schedule] ${e.id} -> $t")
                }

            // A gate that the EVENT already requires gates nothing. It reads on screen as a
            // build-specific option and is on every menu, which is both a lie to the player and a
            // silent hit to option-set differentiation (K4): the metric counts it as variation
            // that no build can ever lack. `fuel_for_return / 'Make more'` required ISRU_FUEL
            // inside an event that requires ISRU_FUEL.
            for (o in e.options) {
                val redundant = o.requires.filter { e.requires.contains(it) }
                if (redundant.isNotEmpty()) errs.add(
                    "[fake-gate] ${e.id} / '${o.label}' requires ${redundant} which the event " +
                            "already requires \u2014 the option is ungated and looks gated")
            }

            // A1 §14.2 / D008: an option may not argue in a currency the event has no occasion in.
            // "Eleven per cent of the loop's power, freed today" is an argument only to a colony
            // that is short of power; offered to one that is not, it reads as a trade nobody would
            // make \u2014 which is exactly what a playtester asked about `fuel_for_return`. Power is
            // the checkable case because it is the one quantity that CANNOT be banked: a surplus
            // is worth nothing next year. Spares, data and medicine keep, so a gain of them needs
            // no occasion.
            // Narrowed twice, both times because the wider rule was wrong: SIM and EMITTED events
            // are pushed by physics, so the incident IS the occasion (a dust storm is a power
            // emergency by construction), and an option whose gain is power AND something else
            // still has an argument when power is comfortable. What is left is the exact shape a
            // playtester read as absurd: a tension-drawn event offering nothing but power.
            if (e.source == "PRESSURE" && e.requires.none { it.contains("sim.powerRatio") })
                for (o in e.options) {
                    val gains = (o.effects + o.outcomes.flatMap { it.effects }).filter { eff ->
                        Regex("""^(res|pop|gauge)\.[A-Za-z]+\s*\+=""").containsMatchIn(eff.trim()) ||
                        Regex("""^pressure\.[A-Za-z]+\s*-=""").containsMatchIn(eff.trim()) ||
                        eff.trim().startsWith("ambition:")
                    }
                    if (gains.isNotEmpty() && gains.all { it.trim().startsWith("res.energy") })
                        advisories.add(
                            "[no-occasion] ${e.id} / '${o.label}' offers freed power to a colony " +
                                    "that is not required to be short of it \u2014 gate the event on " +
                                    "sim.powerRatio, or make the option's case in something else")
                }

            // The subject of the event is a stock, and no option touches it. `fuel_for_return` was
            // titled "The propellant question", spent an ascent load, and left `res.deltaV` \u2014 which
            // is on the stores line, reading "none" \u2014 exactly where it was. Deliberately a tiny
            // lexicon: only nouns that ARE a stock rather than nouns a stock might be involved in,
            // because "water" appears as scenery in half the corpus.
            if (e.kindE != EventKind.SYMPTOM && e.kindE != EventKind.NOTICE) {
                val moved = e.options.flatMap { it.effects + it.outcomes.flatMap { o -> o.effects } }
                for ((stock, rx) in STOCK_NOUNS)
                    if (rx.containsMatchIn(e.body) && moved.none { it.trim().startsWith("res.$stock") })
                        advisories.add("[stock-not-moved] ${e.id} is about ${stock} \u2014 its body says " +
                                "'${rx.find(e.body)!!.value}' \u2014 and no option moves res.$stock")
            }

            // \u00a714.1, the closing side: an option whose text promises permanence must leave a
            // permanent trace. "Nobody leaves this planet again, ever" cost eight morale and set a
            // flag; "forever" with no flag, no module change and no ambition is a promise the
            // engine cannot keep, because next decade nothing will remember it.
            for (o in e.options) {
                val lasting = (o.effects + o.outcomes.flatMap { it.effects }).any { eff ->
                    val t = eff.trim()
                    t.startsWith("flag:") || t.startsWith("ambition:") || t.startsWith("damage:") ||
                            t.startsWith("phase:") || t.startsWith("schedule:") ||
                            t.startsWith("install:") || t.startsWith("grant:") || t.startsWith("revoke:")
                }
                if (!lasting) FOREVER.find(o.label + " " + o.hint)?.let {
                    advisories.add("[unenforced-forever] ${e.id} / '${o.label}' says '${it.value}' " +
                            "but leaves no trace anything can read later")
                }
            }
        }

        // AN INSTITUTION CANNOT BE GRANTED TWICE. An option that establishes something permanent
        // writes a flag; if the event that offers it is not also gated on the flag being absent, it
        // will come round again and demand what the player already gave. Both playtests hit this
        // hard — a council granted in year 19 and used in year 23 was demanded again in years 27
        // and 53; a law was written three times. The reviewers' verdict was not that it repeated
        // but that it FORGOT: *"either the game has forgotten, or the choice never changed the
        // world state — and the second is worse."*
        //
        // An error, not an advisory: it is mechanical, it is checkable, and at 1000 events it will
        // otherwise happen hundreds of times.
        //
        // Scoped to flags that are INSTITUTIONS — ones some other piece of content gates on. A flag
        // nobody reads is just a memory of a thing that happened, and a colony can put down two
        // mutinies; a flag that opens options is a standing arrangement, and offering to establish
        // it twice is the amnesia the readers actually objected to.
        val institutions = corpus.events.flatMap { e ->
            (e.requires + e.options.flatMap { it.requires }).flatMap { it.split('|') }
        }.map { it.trim().removePrefix("!") }
            .filter { it.startsWith("flag:") }.map { it.removePrefix("flag:") }.toSet()
        for (e in corpus.events) {
            if (e.once) continue
            for (o in e.options) {
                val gatesOn = (e.requires + o.requires).flatMap { it.split('|') }.map { it.trim() }.toSet()
                val sets = (o.effects + o.outcomes.flatMap { it.effects }).mapNotNull {
                    Regex("""^flag:([A-Za-z0-9_]+)\s*=\s*true$""").find(it.trim())?.groupValues?.get(1)
                }
                // If ANY flag this option sets also gates it shut, the option cannot come round
                // again — which is all the rule asks. An option that settles a question by setting
                // both `ai_triage` and `triage_settled` is gated by the latter.
                if (sets.any { gatesOn.contains("!flag:$it") }) continue
                for (f in sets) {
                    if (!institutions.contains(f)) continue
                    errs.add("[re-granting] ${e.id} / '${o.label}' establishes '$f', which other " +
                             "content gates on, but nothing stops this being offered again once it " +
                             "is set — require !flag:$f on the option, or mark the event once")
                }
            }
        }

        // AN EVENT THAT CAN FIRE SIX TIMES A CENTURY NEEDS MORE THAN ONE FACE.
        //
        // Four playtests put repetition at the top of every list, and never because of the
        // repetition itself — because of the AMNESIA: a body written for a first time, printed for
        // a fifth. `volunteer_outside` has a nine-year cooldown, fired five times in one run, and
        // carries exactly ONE variant, so firings two through five were byte-identical.
        // `ration_table` fired three times with none at all.
        //
        // A century divided by the cooldown is how many times the player can see this, so that is
        // what the requirement is written against. The engine cycles variants, so N variants covers
        // N+1 firings.
        for (e in corpus.events) {
            if (e.once || e.kindE == EventKind.SYMPTOM) continue
            // Graded by cooldown rather than by the theoretical maximum. 100/cooldown is what the
            // rules ALLOW; eligibility means the hottest events in four playtests actually fired
            // three to six times. Asking for three variants on everything would be authoring for a
            // frequency the game does not produce.
            val canFire = if (e.cooldownYears <= 0) 99 else 100 / e.cooldownYears
            var want = when {
                e.cooldownYears <= 10 -> 3
                e.cooldownYears <= 16 -> 2
                e.cooldownYears <= 30 -> 1
                else -> 0
            }
            // An event whose own text carries a slot — "{subsystemName} has failed", "{crop} is
            // failing" — already reads differently every firing, because the binding names a
            // different thing each time. That is a variant by another route, so it counts as one.
            if (Regex("""\{[a-zA-Z]+\}""").containsMatchIn(e.title + e.body)) want -= 1
            if (want <= 0) continue
            if (e.variants.size < want)
                errs.add("[thin-variants] ${e.id} has a ${e.cooldownYears}y cooldown, so it can fire " +
                         "$canFire times in a century, and carries ${e.variants.size} variant(s); " +
                         "needs $want so a repeat does not read as a first time")
        }

        // EVERY EVENT MUST OFFER AT LEAST ONE WAY TO GAIN SOMETHING VISIBLE.
        //
        // `[pure-cost]` flags an option that offers nothing; it is an advisory because a deferred
        // payoff is legitimate. But an EVENT where NO option offers anything is loss allocation
        // with extra steps, and 45 such options across the corpus are why a century of governing
        // measured as net-negative once decisions started to persist (D007 §4: "there is
        // essentially no positive economy... you cannot build an engine here").
        for (e in corpus.events) {
            if (e.kindE == EventKind.SYMPTOM || e.kindE == EventKind.NOTICE) continue
            val anyGain = e.options.any { o ->
                (o.effects + o.outcomes.flatMap { it.effects }).any { eff ->
                    val t = eff.trim()
                    Regex("""^(res|pop|gauge)\.[A-Za-z]+\s*\+=""").containsMatchIn(t) ||
                    Regex("""^pressure\.[A-Za-z]+\s*-=""").containsMatchIn(t) ||
                    Regex("""^damage:\S+\s+-""").containsMatchIn(t) ||
                    t.startsWith("install:") || t.startsWith("grant:") || t.startsWith("ambition:")
                }
            }
            if (!anyGain) errs.add("[no-gain] ${e.id}: not one of its ${e.options.size} options " +
                    "offers the player anything visible — this is a bill, not a decision")
        }

        // THE GAUGES THAT FEED PEOPLE MUST BE BUYABLE.
        //
        // `[no-gain]` catches an event where every option is a deduction. It cannot catch a corpus
        // that merely leans, and this one leaned hard in the one direction that matters:
        //
        //   morale      63 options raise it,  98 lower it   net -214     labour weight 0.40
        //   health      18 options raise it,  29 lower it   net  -62     labour weight 0.40
        //   cohesion    60 options raise it,   9 lower it   net +345     labour weight 0.15
        //   competence  67 options raise it,  15 lower it   net +274     labour weight 0.00
        //
        // The magnitudes were symmetric; the COUNTS were not. So a century of play accumulated the
        // two gauges that do almost nothing and spent the two that drive `Physics.labourIndex`,
        // which is what feeds everyone. That is D007's "there is essentially no positive economy...
        // you cannot build an engine here" — located, and made a number.
        //
        // A colony must be able to convert material into willingness and wellbeing, because that is
        // what governing a closed community actually consists of: food, rest, ceremony, privacy,
        // medicine. Not free — every such option pays in something the status panel shows.
        run {
            val net = mutableMapOf<String, Double>()
            for (e in corpus.events) for (o in e.options)
                for (eff in o.effects + o.outcomes.flatMap { it.effects }) {
                    val m = Regex("""^gauge\.([A-Za-z]+)\s*(\+=|-=)\s*([0-9.]+)$""").find(eff.trim())
                        ?: continue
                    val v = m.groupValues[3].toDouble() * (if (m.groupValues[2] == "-=") -1 else 1)
                    net[m.groupValues[1]] = (net[m.groupValues[1]] ?: 0.0) + v
                }
            // Only the gauges Physics.labourIndex actually reads. A corpus may lean on legitimacy
            // for ever — that is a story about authority, and it starves nobody.
            for (g in listOf("morale", "health")) {
                val v = net[g] ?: 0.0
                if (v < 0) errs.add("[gauge-economy] the corpus is net ${Fmt.int(v)} on '$g', " +
                        "which drives labour and therefore food. Playing well must be able to hold " +
                        "it level: add options that BUY $g with something the player can see spent")
            }
        }

        // A stand-in must name a beat that exists, or it can never be drawn.
        for (e in corpus.events) {
            if (e.stageOf.isBlank()) continue
            if (byId[e.stageOf] == null)
                errs.add("[dangling-stage] ${e.id} declares stageOf='${e.stageOf}', which is not an event")
            if (e.stageOf == e.id)
                errs.add("[dangling-stage] ${e.id} declares itself as its own stand-in")
        }

        // A BUILD-GATED EVENT MUST STILL VARY WITHIN A RUN.
        //
        // D002 §0 (amended 2026-08-30) makes module- and capability-gated EVENTS the intended way
        // the deck differs per playthrough. The risk the designer named in the same breath is that
        // a build then sees a smaller, more static deck — the same events at the same moments every
        // time you fly that ship.
        //
        // What stops it is that a build-gated event must ALSO gate on something that moves during a
        // run. `cap:DRONE` alone fires in every drone playthrough at roughly the same point.
        // `cap:DRONE` plus `pressure.xenoExposure >= 0.35` fires only in the drone runs where that
        // pressure actually built — build-gated AND state-gated, so the deck differs across builds
        // and across runs of the same build.
        //
        // This is why `same-build event-set similarity` sits at 0.50 while builds share 82% of the
        // corpus: the deck is already gated overwhelmingly on run state (100 pressure gates against
        // 19 capability gates). The rule keeps that true as module-gated content is added.
        //
        // Preferred, and not enforceable here: gate on what the colony BUILT rather than what it
        // launched with. A flag or an acquired capability makes two runs of one build diverge
        // because of what the player DID, which is the kind of variety the game is actually for.
        for (e in corpus.events) {
            if (e.kindE == EventKind.SYMPTOM) continue
            val terms = e.requires.flatMap { it.split('|') }.map { it.trim().removePrefix("!") }
            if (terms.isEmpty()) continue
            val buildish = terms.any {
                it.startsWith("cap:") || it.startsWith("mod.") || it.startsWith("planet.")
            }
            // Anything that can change while a colony is running.
            val dynamic = terms.any {
                it.startsWith("pressure.") || it.startsWith("flag:") || it.startsWith("res.") ||
                it.startsWith("pop.") || it.startsWith("gauge.") || it.startsWith("sim.") ||
                it.startsWith("axis.") || it.startsWith("year") || it.startsWith("month")
            }
            if (buildish && !dynamic)
                errs.add("[static-deck] ${e.id} is gated only on what the expedition IS " +
                        "(${terms.filter { it.startsWith("cap:") || it.startsWith("mod.") || it.startsWith("planet.") }
                            .joinToString(", ")}), so it fires at the same point in every run of " +
                        "that build. Add a gate on something that moves — a pressure, a flag, a " +
                        "stock, a sim reading or a year (D002 §0)")
        }

        // A CAPABILITY IS NOT A DISCOUNT. Using one costs the labour or power it takes to use it.
        //
        // D007 §7 predicted a degenerate strategy and named it — "the Quiet Bureaucrat... pay in
        // words, never in matter; route every social question through vote, records or chapel" —
        // and the corpus turned out to be built to reward it: 40 of 137 capability-gated options
        // had no visible cost at all, and the free ones were every social and informational verb in
        // the game (records, lab, core, vote, surveillance, chapel, archive) while every physical
        // one charged spares. `tools/gen_content.py` prices them from one table now; this stops the
        // next thousand events from reintroducing the hole one option at a time.
        for (e in corpus.events) {
            if (e.kindE == EventKind.SYMPTOM) continue
            for (o in e.options) {
                val caps = o.requires.flatMap { it.split('|') }.map { it.trim() }
                    .filter { it.startsWith("cap:") }
                if (caps.isEmpty()) continue
                val effs = o.effects + o.outcomes.flatMap { it.effects }
                val pays = effs.any { eff ->
                    val t = eff.trim()
                    Regex("""^(res|pop)\.[A-Za-z]+\s*-=""").containsMatchIn(t) ||
                    Regex("""^gauge\.[A-Za-z]+\s*-=""").containsMatchIn(t) ||
                    t.startsWith("kill:") || t.startsWith("damage:")
                }
                if (!pays) errs.add("[free-privilege] ${e.id} / '${o.label}' is gated on " +
                        caps.joinToString(" or ") + " and costs the player nothing visible; " +
                        "a capability opens a different answer, it is not a discount")
            }
        }

        // A2, legibility half: an option that offers the player NOTHING is a trap dressed as a
        // choice. Its real payoff may be genuine but deferred (forbidding births makes the stores
        // last), and a deferred payoff the player cannot see is indistinguishable from no payoff.
        // Advisory rather than error, because deferred payoffs are legitimate — but then the HINT
        // must carry the argument, and that is a human check.
        for (e in corpus.events) {
            if (e.kindE == EventKind.SYMPTOM || e.kindE == EventKind.NOTICE) continue
            for (o in e.options) {
                val all = o.effects + o.outcomes.flatMap { it.effects }
                val gives = all.any { eff ->
                    Regex("""^(res|pop|gauge)\.[A-Za-z]+\s*\+=""").containsMatchIn(eff.trim()) ||
                    Regex("""^pressure\.[A-Za-z]+\s*-=""").containsMatchIn(eff.trim()) ||
                    Regex("""^damage:\S+\s+-""").containsMatchIn(eff.trim())
                }
                if (!gives) advisories.add(
                    "[pure-cost] ${e.id} / '${o.label}' offers the player nothing visible; " +
                    "the hint must carry the argument for it")
            }
        }

        // D003 P4: every pressure needs symptom events, or hidden state cannot telegraph at all.
        // 3 is the hard floor. Measured symptom-repetition (docs/PLAYTEST-LOG.md) says the SHIPPING
        // corpus needs ~8-10 per pressure to stop the same mood line recurring; below that is a
        // known, quantified content debt rather than a structural fault.
        for (p in PRESSURES) {
            val n = corpus.events.count { it.kindE == EventKind.SYMPTOM && it.symptomOf == p }
            if (n < 3) errs.add("[thin-symptoms] pressure '$p' has $n symptom events, needs >= 3")
            else if (n < 8) advisories.add("[symptom-debt] pressure '$p' has $n symptoms; ship target is 8-10")
        }
        // THE OTHER HALF OF ATTRIBUTION. A `flag:` is how a decision survives to be referred to
        // later; a flag that nothing reads is a decision the game recorded and then forgot. The
        // player is told "this will be remembered" by the shape of the choice, and it is not. This
        // is a content debt rather than a fault — the flag is harmless — so it is an advisory, but
        // it is the direct measure of how much of the game's memory is decorative.
        run {
            val flagSet = Regex("""^flag:([A-Za-z0-9_]+)\s*=""")
            val set = mutableMapOf<String, MutableSet<String>>()
            val read = mutableSetOf<String>()
            for (e in corpus.events) {
                for (r in e.requires + e.options.flatMap { it.requires })
                    for (alt in r.split('|')) {
                        val a = alt.trim().trimStart('!')
                        if (a.startsWith("flag:")) read.add(a.removePrefix("flag:").trim())
                    }
                for (o in e.options)
                    for (eff in o.effects + o.outcomes.flatMap { it.effects })
                        flagSet.find(eff.trim())?.let {
                            set.getOrPut(it.groupValues[1]) { mutableSetOf() }.add(e.id)
                        }
            }
            // Flags the SIMULATION reads are not unread — `births_forbidden` changes the birth
            // rate, `planet_scanned` changes what is knowable, `earth_silent` cuts the channel.
            // Kept as an explicit list because Physics reads them by string, exactly like the
            // module modifiers: if one is renamed, this list is the thing that notices.
            val engineReads = setOf("arrived", "landed", "expedition_begun", "planet_scanned",
                "births_forbidden", "births_rationed", "earth_silent",
                "geothermal", "ironworks", "deep_water", "bio_industry")
            for ((f, where) in set.toList().sortedBy { it.first })
                if (f !in read && f !in engineReads) advisories.add(
                    "[unread-flag] '$f' is set by ${where.sorted()} and read by no event — a " +
                            "decision the game remembers and never mentions again")
        }

        // A GATE THAT IS TRUE AT LAUNCH IS NOT A GATE.
        //
        // `[unfounded-opportunity]` asks whether an opportunity requires the slack it claims. It
        // never asked whether the requirement is ever FALSE — and `sim.fed >= 1.6` is not, because
        // the twelve reference builds launch at fed 4.6 to 53. So "the stores are fuller than the
        // plan requires" was being said about the plan's own reserve, in month one, to every build:
        // three of the four early-game opportunities were the most-fired events of year 0.
        //
        // Every build is instantiated at launch and the corpus is evaluated against it, so this is
        // measured rather than argued. (~12 throwaway WorldStates; validate stays well under a
        // second.) Playtest 2026-08-28, `PLAYTEST-LOG.md` §1g.
        if (corpus.builds.isNotEmpty() && corpus.planets.isNotEmpty()) {
            val rng = kotlin.random.Random(1)
            val launched = corpus.builds.mapNotNull { b ->
                try {
                    val cata = corpus.catastrophes.firstOrNull { it.build == b.id }
                    val planet = corpus.planets.firstOrNull { it.id == cata?.planet } ?: corpus.planets[0]
                    val st = WorldState(b, planet, "m", "c", cata)
                    Setup.begin(st, rng)
                    st to Physics.capabilities(st)
                } catch (e: Exception) { null }
            }
            for (e in corpus.events) {
                if (!CLAIMS_DEPARTURE.containsMatchIn(e.body + " " + e.title)) continue
                val live = launched.filter { (st, caps) ->
                    (e.phase.contains("ANY") || e.phase.contains(st.phase.name)) &&
                            try { Expr.all(st, caps, e.requires) } catch (ex: Exception) { false }
                }
                if (live.isNotEmpty()) errs.add(
                    "[launch-slack] ${e.id} says the colony has departed from normal, but its " +
                            "requirements are already satisfied by ${live.size} of ${launched.size} " +
                            "builds AS LAUNCHED — the claim is about the design margin")
            }
        }

        // NO OPTION MAY BE WORSE THAN A SIBLING ON EVERY AXIS. (A2 no dominant option; A5 fake
        // choice.) Found by playtest as "I don't understand the difference between these two" and
        // as "I see no reason to say yes here" — and the cause was usually that an option STATED a
        // benefit the effects did not contain: all three coercive answers to `work_stoppage`
        // promised "the harvest is saved" and returned no food at all, so the humane answer beat
        // them on every visible number. Ten pairs on first run; the corpus is now clean.
        //
        // Compares signed effect vectors (a rising pressure counts as a cost), only between options
        // with identical non-numeric effects (a flag or a phase change is a real differentiator)
        // and only where the better option is available whenever the worse one is. An option with
        // `outcomes` is exempt: a declared gamble is not comparable to a certainty, and turning a
        // dominated option into an honest gamble is one of the two fixes.
        for (e in corpus.events) {
            fun vector(o: Option): Pair<Map<String, Double>, Set<String>>? {
                if (o.outcomes.isNotEmpty()) return null
                val v = mutableMapOf<String, Double>()
                val extras = mutableSetOf<String>()
                for (raw in o.effects) {
                    val m = Regex("""^(res|pop|gauge|pressure)\.([A-Za-z]+)\s*(\+=|-=)\s*([0-9.]+)$""")
                        .find(raw.trim())
                    if (m == null) { extras.add(raw.trim()); continue }
                    val (ns, k, op, num) = m.destructured
                    var sign = if (op == "+=") 1.0 else -1.0
                    if (ns == "pressure") sign = -sign
                    v["$ns.$k"] = (v["$ns.$k"] ?: 0.0) + sign * num.toDouble()
                }
                return v to extras
            }
            for (a in e.options) for (b in e.options) {
                if (a === b) continue
                val (va, ea) = vector(a) ?: continue
                val (vb, eb) = vector(b) ?: continue
                // WAS `ea != eb`, REQUIRING THE NON-NUMERIC EFFECTS TO MATCH EXACTLY -- so an
                // option that did everything a sibling did AND set a flag never counted as
                // dominating it, and the rule found 1 case where the correct test finds 9. What
                // matters is that `b` offers nothing `a` does not: a superset of extras on the
                // winner is still a win. The requires check stays as it was, and it is what makes
                // this safe for the design -- `a` only counts if it is offered WHENEVER `b` is, so
                // an option unlocked by a module, a capability or an earlier decision is exempt by
                // construction. An earned advantage is allowed to be strictly better (D007).
                if (!ea.containsAll(eb) || !b.requires.containsAll(a.requires)) continue
                val keys = va.keys + vb.keys
                if (keys.isEmpty()) continue
                val everywhere = keys.all { (va[it] ?: 0.0) >= (vb[it] ?: 0.0) }
                val somewhere = keys.any { (va[it] ?: 0.0) > (vb[it] ?: 0.0) }
                if (everywhere && somewhere) advisories.add(
                    "[dominated-option] ${e.id}: '${a.label}' is at least as good as " +
                            "'${b.label}' on every axis \u2014 give the worse one the benefit it " +
                            "claims, or make it a declared gamble")
            }
        }

        // A PARAMETER NO CONTENT READS IS VOCABULARY NOBODY LEARNS. Same shape as the unused-cap
        // rule below, one rung softer: a readable path costs a line in the authoring guide, a line
        // in the interface if it is to be visible at all (A3 named quantities), and a reader's
        // attention — and it earns that at about three events. One summary line rather than thirty
        // advisories, because most of this list is known debt (no content reads an identity axis).
        run {
            val used = mutableSetOf<String>()
            val cmp = Regex("""([A-Za-z_][A-Za-z0-9_.]*)\s*(<=|>=|==|!=|<|>)""")
            for (e in corpus.events)
                for (r in e.requires + e.options.flatMap { it.requires })
                    for (alt in r.split('|'))
                        cmp.find(alt.trim().trimStart('!'))?.let { used.add(it.groupValues[1]) }
            val idle = READABLE.filter { it !in used }.sorted()
            if (idle.isNotEmpty()) advisories.add(
                "[unused-path] ${idle.size} of ${READABLE.size} readable parameters are never " +
                        "gated on by any event: $idle")
        }

        // D003 P1: orphan detection — a capability nothing uses is dead vocabulary.
        // Alternation-aware: "cap:SCAN_ATMO | cap:DRONE" uses BOTH capabilities.
        fun requiredCaps(o: Option): Set<String> = o.requires
            .flatMap { it.split('|') }
            .map { it.trim().trimStart('!') }
            .filter { it.startsWith("cap:") }
            .map { it.removePrefix("cap:") }
            .toSet()
        for (c in capIds) {
            val used = corpus.events.any { e -> e.options.any { o -> requiredCaps(o).contains(c) } }
            val granted = corpus.modules.any { it.grants.contains(c) }
            if (!used) errs.add("[unused-cap] $c is granted but no option requires it")
            if (!granted && c !in setOf("MANPOWER", "EXPERTISE")) errs.add("[ungranted-cap] $c required but no module grants it")
        }
        return errs
    }
}
